import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Scorecard, ScorecardResult } from '../../models/Scorecard'
import { hasTenMinutesPassed } from '../utils'
import { RootState } from '../store'

import { selectEmailThreads, selectEmailThreadsLoading } from './emailThreads'

interface EmailResponseClientProfileState {
  results: ScorecardResult[]
  resultsLoading: boolean
  scorecards: Scorecard[]
  scorecardsLoading: boolean
  resultPollingEnabled: boolean
}

const initialState: EmailResponseClientProfileState = {
  results: [],
  resultsLoading: true,
  scorecards: [],
  scorecardsLoading: true,
  resultPollingEnabled: false,
}

export const emailResponseClientProfileSlice = createSlice({
  name: 'emailResponseClientProfile',
  initialState,
  reducers: {
    setEmailResponseClientProfileResults: (state, action: PayloadAction<ScorecardResult[]>) => {
      state.results = action.payload
    },
    setEmailResponseClientProfileResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.resultsLoading = action.payload
    },
    setEmailResponseClientProfileScorecards: (state, action: PayloadAction<Scorecard[]>) => {
      state.scorecards = action.payload
    },
    setEmailResponseClientProfileScorecardsLoading: (state, action: PayloadAction<boolean>) => {
      state.scorecardsLoading = action.payload
    },
    startEmailResponseClientProfileResultPolling: (state) => {
      state.resultPollingEnabled = true
    },
    stopEmailResponseClientProfileResultPolling: (state) => {
      state.resultPollingEnabled = false
    },
    resetEmailResponseClientProfile: () => initialState,
  },
})

export const {
  setEmailResponseClientProfileResults,
  setEmailResponseClientProfileResultsLoading,
  setEmailResponseClientProfileScorecards,
  setEmailResponseClientProfileScorecardsLoading,
  startEmailResponseClientProfileResultPolling,
  stopEmailResponseClientProfileResultPolling,
  resetEmailResponseClientProfile,
} = emailResponseClientProfileSlice.actions

export const selectEmailResponseClientProfileResults = (state: RootState) => state.emailHub.emailResponseClientProfile.results

export const selectEmailResponseClientProfileResultsLoading = (state: RootState) => state.emailHub.emailResponseClientProfile.resultsLoading

export const selectEmailResponseClientProfileScorecards = (state: RootState) => state.emailHub.emailResponseClientProfile.scorecards

export const selectEmailResponseClientProfileScorecardsLoading = (state: RootState) => state.emailHub.emailResponseClientProfile.scorecardsLoading

export const selectEmailResponseClientProfileResultPollingEnabled = (state: RootState) => state.emailHub.emailResponseClientProfile.resultPollingEnabled

export const selectShouldPollEmailResponseClientProfileResults = createSelector(
  [
    selectEmailResponseClientProfileResultsLoading,
    selectEmailResponseClientProfileResults,
    selectEmailThreadsLoading,
    selectEmailThreads,
    (_: RootState, threadId: string) => threadId,
  ],
  (clientProfileResultsLoading, clientProfileResults, threadsLoading, threads, threadId) => {
    if (clientProfileResultsLoading || threadsLoading) {
      return false
    }
    const thread = threads.find((e) => e.externalId === threadId)
    if (!thread) {
      return false
    }
    const sortedEmails = thread.emails.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    return clientProfileResults.length === 0 && !hasTenMinutesPassed(sortedEmails[0].createdAt)
  },
)
